import React, { useState, useEffect, useCallback, useRef } from 'react';

// Voiceover audio files per shot — add entries as files are uploaded
const SHOT_VOICEOVER = {
  1: '/audio/shot1-voiceover.mp3',
  2: '/audio/shot2-voiceover.mp3',
  3: '/audio/shot3-voiceover.mp3',
  4: '/audio/shot5-voiceover.mp3',
  5: '/audio/shot6-voiceover.mp3',
  6: '/audio/shot4-voiceover.mp3',
  7: '/audio/shot7-voiceover.mp3',
};

// Fallback durations (ms) for shots without voiceover audio
const FALLBACK_DURATIONS = [8000, 7000, 9000, 11000, 11000, 7000, 12000];

// Text for each shot
const SHOT_TEXT = {
  1: ["My Dearest Eva"],
  2: [
    "I know this is not an easy time for you",
    "I know I am not by your side",
    "The box is yours now"
  ],
  3: [
    "My gaze had become blazing hot",
    "The rays flared out of my eyes and ripped through the air"
  ],
  4: ["I was a freak of nature with fire in my eyes"],
  5: [
    "In the middle of the storm of bodies",
    "Grams and I were still",
    "We were both smiling. So hard."
  ],
  6: [
    "He wasn't just an observer of the stars",
    "The stars lived within him",
    "They gave him power"
  ],
  7: [
    "Just as the seasons change, so must we",
    "Just as leaves will fall, so will green shoots sprout",
    "Choose the ones that speak to you"
  ],
};

export default function IntroSequence({ onComplete }) {
  const [phase, setPhase] = useState('title');
  const [currentShot, setCurrentShot] = useState(0);
  const [shotProgress, setShotProgress] = useState(0);
  const [titleOpacity, setTitleOpacity] = useState(1);
  const [boxWaiting, setBoxWaiting] = useState(false);
  const [boxOpening, setBoxOpening] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const soundtrackRef = useRef(null);
  const voiceoverRef = useRef(null);
  const shotStartRef = useRef(null);
  const shotDurationRef = useRef(null);
  const animFrameRef = useRef(null);
  const fallbackTimerRef = useRef(null);

  // Advance to next shot with cross-fade
  const advanceToNext = useCallback(() => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentShot(prev => prev + 1);
      setTimeout(() => setTransitioning(false), 50);
    }, 900);
  }, []);

  // Detect completion when currentShot reaches 7
  useEffect(() => {
    if (currentShot < 7) return;
    setPhase('complete');
    // Fade out soundtrack gracefully
    if (soundtrackRef.current) {
      const audio = soundtrackRef.current;
      const fade = setInterval(() => {
        if (audio.volume > 0.05) {
          audio.volume = Math.max(0, audio.volume - 0.05);
        } else {
          audio.pause();
          clearInterval(fade);
        }
      }, 100);
    }
    const t = setTimeout(() => onComplete(), 600);
    return () => clearTimeout(t);
  }, [currentShot, onComplete]);

  // Start the intro on tap
  const handleStart = useCallback(() => {
    if (phase !== 'title') return;
    const soundtrack = new Audio('/audio/soundtrack.mp3');
    soundtrack.volume = 0.4;
    soundtrack.loop = true;
    soundtrackRef.current = soundtrack;
    soundtrack.play().catch(() => {});

    setTitleOpacity(0);
    setTimeout(() => setPhase('playing'), 1000);
  }, [phase]);

  // Handle tapping the golden box in Shot 7
  const handleBoxTap = useCallback(() => {
    if (boxOpening) return;
    setBoxOpening(true);
    // Fade out soundtrack
    if (soundtrackRef.current) {
      const audio = soundtrackRef.current;
      const fade = setInterval(() => {
        if (audio.volume > 0.05) {
          audio.volume = Math.max(0, audio.volume - 0.05);
        } else {
          audio.pause();
          clearInterval(fade);
        }
      }, 60);
    }
    // Wait for box open animation, then transition to game
    setTimeout(() => {
      setPhase('complete');
      onComplete();
    }, 1800);
  }, [boxOpening, onComplete]);

  // Set up voiceover or fallback timer when shot changes
  useEffect(() => {
    if (phase !== 'playing' || currentShot >= 7) return;

    const shotNum = currentShot + 1;
    shotStartRef.current = Date.now();
    shotDurationRef.current = FALLBACK_DURATIONS[currentShot];
    setShotProgress(0);

    const audioSrc = SHOT_VOICEOVER[shotNum];
    if (audioSrc) {
      const audio = new Audio(audioSrc);
      voiceoverRef.current = audio;

      // Dip soundtrack while voiceover plays
      if (soundtrackRef.current) soundtrackRef.current.volume = 0.15;

      audio.addEventListener('loadedmetadata', () => {
        shotDurationRef.current = audio.duration * 1000;
      });

      audio.addEventListener('ended', () => {
        if (soundtrackRef.current) soundtrackRef.current.volume = 0.4;
        voiceoverRef.current = null;
        if (currentShot === 6) {
          setBoxWaiting(true);
        } else {
          advanceToNext();
        }
      });

      audio.addEventListener('error', () => {
        if (soundtrackRef.current) soundtrackRef.current.volume = 0.4;
        voiceoverRef.current = null;
        const action = currentShot === 6 ? () => setBoxWaiting(true) : advanceToNext;
        fallbackTimerRef.current = setTimeout(action, FALLBACK_DURATIONS[currentShot]);
      });

      audio.play().catch(() => {
        if (soundtrackRef.current) soundtrackRef.current.volume = 0.4;
        voiceoverRef.current = null;
        const action = currentShot === 6 ? () => setBoxWaiting(true) : advanceToNext;
        fallbackTimerRef.current = setTimeout(action, FALLBACK_DURATIONS[currentShot]);
      });
    } else {
      // No voiceover — use fallback timer
      const action = currentShot === 6 ? () => setBoxWaiting(true) : advanceToNext;
      fallbackTimerRef.current = setTimeout(action, FALLBACK_DURATIONS[currentShot]);
    }

    return () => {
      if (voiceoverRef.current) {
        voiceoverRef.current.pause();
        voiceoverRef.current = null;
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [phase, currentShot, advanceToNext]);

  // Animation loop — sync progress to voiceover position or elapsed time
  useEffect(() => {
    if (phase !== 'playing' || currentShot >= 7) return;

    const animate = () => {
      const audio = voiceoverRef.current;
      let progress;
      if (audio && audio.duration && !isNaN(audio.duration) && audio.duration > 0) {
        progress = audio.currentTime / audio.duration;
      } else {
        const elapsed = Date.now() - (shotStartRef.current || Date.now());
        const duration = shotDurationRef.current || FALLBACK_DURATIONS[currentShot];
        progress = elapsed / duration;
      }
      setShotProgress(Math.min(1, Math.max(0, progress)));
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [phase, currentShot]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (soundtrackRef.current) soundtrackRef.current.pause();
      if (voiceoverRef.current) voiceoverRef.current.pause();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  return (
    <div
      onClick={handleStart}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#060e18',
        cursor: phase === 'title' ? 'pointer' : 'default',
        overflow: 'hidden',
      }}
    >
      {/* WAKE Title Screen - shown first */}
      {phase === 'title' && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: titleOpacity,
          transition: 'opacity 1s ease-out',
        }}>
          <h1 style={{
            fontFamily: "'Pixelify Sans', monospace",
            fontSize: 'clamp(48px, 12vw, 120px)',
            color: '#c9a84c',
            letterSpacing: '0.3em',
            margin: 0,
            textShadow: '0 0 40px rgba(201,168,76,0.5), 0 0 80px rgba(201,168,76,0.3), 0 4px 0 #8a7030',
            animation: 'titleGlow 3s ease-in-out infinite',
          }}>
            WAKE
          </h1>
          <p style={{
            fontFamily: "'Caveat', cursive",
            fontSize: 'clamp(18px, 3vw, 28px)',
            color: 'rgba(201, 168, 76, 0.7)',
            marginTop: '40px',
            animation: 'tapPulse 2s ease-in-out infinite',
          }}>
            tap to begin
          </p>
        </div>
      )}

      {/* ── Cross-fade wrapper for all shots ── */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: transitioning ? 0 : 1,
        transition: 'opacity 0.9s ease-in-out',
      }}>
        {/* Shot 1: The Letter Begins */}
        {phase === 'playing' && currentShot === 0 && (
          <Shot01LetterBegins progress={shotProgress} />
        )}

        {/* Shot 2: Fragments of the Letter */}
        {phase === 'playing' && currentShot === 1 && (
          <Shot02Fragments progress={shotProgress} />
        )}

        {/* Shot 3: The Scorch Marks */}
        {phase === 'playing' && currentShot === 2 && (
          <Shot03ScorchMarks progress={shotProgress} />
        )}

        {/* Shot 4: Carnival Whirl */}
        {phase === 'playing' && currentShot === 3 && (
          <Shot05Carnival progress={shotProgress} />
        )}

        {/* Shot 5: Explosion of Stars */}
        {phase === 'playing' && currentShot === 4 && (
          <Shot06Stars progress={shotProgress} />
        )}

        {/* Shot 6: Fire in Her Eyes */}
        {phase === 'playing' && currentShot === 5 && (
          <Shot04FireEyes progress={shotProgress} />
        )}

        {/* Shot 7: Spirals into Box */}
        {phase === 'playing' && currentShot === 6 && (
          <Shot07SpiralsBox
            progress={shotProgress}
            boxWaiting={boxWaiting}
            boxOpening={boxOpening}
            onBoxTap={handleBoxTap}
          />
        )}
      </div>


      <style>{`
        @keyframes tapPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes titleGlow {
          0%, 100% { text-shadow: 0 0 40px rgba(201,168,76,0.5), 0 0 80px rgba(201,168,76,0.3), 0 4px 0 #8a7030; }
          50% { text-shadow: 0 0 60px rgba(201,168,76,0.7), 0 0 100px rgba(201,168,76,0.5), 0 4px 0 #8a7030; }
        }
        @keyframes flicker {
          0%, 100% { opacity: 0.8; transform: scaleY(1); }
          50% { opacity: 1; transform: scaleY(1.05); }
          25%, 75% { opacity: 0.9; transform: scaleY(0.98); }
        }
        @keyframes drift {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes boxPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(201,168,76,0.4), inset 0 0 15px rgba(201,168,76,0.2); }
          50% { box-shadow: 0 0 40px rgba(201,168,76,0.7), inset 0 0 25px rgba(201,168,76,0.4), 0 0 60px rgba(201,168,76,0.3); }
        }
        @keyframes boxOpenGlow {
          0% { width: 0px; height: 0px; opacity: 0; }
          30% { width: 300px; height: 300px; opacity: 1; }
          100% { width: 300vw; height: 300vh; opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Shot 1: Gold handwriting "My Dearest Eva" appearing letter by letter
function Shot01LetterBegins({ progress }) {
  const text = SHOT_TEXT[1][0];
  const visibleChars = Math.floor(progress * text.length * 1.5);
  
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      
    }}>
      <h2 style={{
        fontFamily: "'Caveat', cursive",
        fontSize: 'clamp(28px, 5vw, 48px)',
        color: '#c9a84c',
        textShadow: '0 0 20px rgba(201,168,76,0.6), 0 0 40px rgba(201,168,76,0.3)',
        letterSpacing: '0.05em',
      }}>
        {text.split('').map((char, i) => (
          <span
            key={i}
            style={{
              opacity: i < visibleChars ? 1 : 0,
              transition: 'opacity 0.3s ease-out',
            }}
          >
            {char}
          </span>
        ))}
      </h2>
    </div>
  );
}

// Shot 2: Letter fragments drifting
function Shot02Fragments({ progress }) {
  const fragments = SHOT_TEXT[2];
  const frameOpacity = Math.max(0, 1 - Math.pow(progress, 3) * 1.2);
  
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '30px',
      
    }}>
      {/* Gold box frame — fades from the screen */}
      <div style={{
        position: 'absolute',
        inset: '8%',
        border: `2px solid rgba(201, 168, 76, ${frameOpacity * 0.7})`,
        borderRadius: '4px',
        boxShadow: `0 0 20px rgba(201,168,76,${frameOpacity * 0.2}), inset 0 0 20px rgba(201,168,76,${frameOpacity * 0.1})`,
        pointerEvents: 'none',
      }} />
      {fragments.map((text, i) => {
        const fragmentProgress = Math.max(0, Math.min(1, (progress * 4) - i * 1.0));
        const pulse = 0.7 + Math.sin(progress * Math.PI * 3 + i * 1.5) * 0.3;
        const blur = Math.abs(Math.sin(progress * Math.PI * 2 + i)) * 2;
        return (
          <p
            key={i}
            style={{
              fontFamily: "'Caveat', cursive",
              fontSize: 'clamp(20px, 3vw, 30px)',
              color: '#c9a84c',
              textShadow: '0 0 15px rgba(201,168,76,0.5)',
              opacity: fragmentProgress * pulse,
              filter: `blur(${blur}px)`,
              transform: `translateY(${(1 - fragmentProgress) * 20}px)`,
              transition: 'all 0.5s ease-out',
              textAlign: 'center',
              maxWidth: '80%',
            }}
          >
            {text}
          </p>
        );
      })}
    </div>
  );
}

// Shot 3: Eva's blazing gaze — beams erupting from bottom centre
function Shot03ScorchMarks({ progress }) {
  const texts = SHOT_TEXT[3];

  // Origin — Eva's eyes, bottom centre, just off frame
  const ox = 50, oy = 101;

  // Generate a jagged zigzag beam path from origin
  const makeBeam = (angle, len, segs, seed, forkSegs) => {
    const step = len / segs;
    const dx = Math.cos(angle) * step;
    const dy = Math.sin(angle) * step;
    const jit = step * 0.38;
    const pts = [{ x: ox, y: oy }];
    let x = ox, y = oy;
    for (let i = 0; i < segs; i++) {
      x += dx + Math.sin(seed + i * 7.3) * jit;
      y += dy + Math.cos(seed + i * 5.1) * jit * 0.6;
      pts.push({ x, y });
    }
    let d = `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x.toFixed(1)},${pts[i].y.toFixed(1)}`;

    // Forks
    const forks = [];
    for (const fi of forkSegs) {
      if (fi >= pts.length) continue;
      const p = pts[fi];
      const fa = angle + Math.sin(seed + fi * 3.7) * 0.6;
      const fl = step * (1.8 + Math.sin(seed + fi) * 0.6);
      const mx = p.x + Math.cos(fa) * fl * 0.5 + Math.sin(seed + fi * 4.1) * jit * 0.3;
      const my = p.y + Math.sin(fa) * fl * 0.5 + Math.cos(seed + fi * 2.9) * jit * 0.3;
      const ex = p.x + Math.cos(fa) * fl;
      const ey = p.y + Math.sin(fa) * fl;
      forks.push(`M ${p.x.toFixed(1)},${p.y.toFixed(1)} L ${mx.toFixed(1)},${my.toFixed(1)} L ${ex.toFixed(1)},${ey.toFixed(1)}`);
    }
    return { d, forks, pts };
  };

  const PI = Math.PI;
  // 3 sky beams, 4 ground beams
  const beams = [
    makeBeam(-PI * 0.40, 88, 10, 1.3, [4, 7]),
    makeBeam(-PI * 0.50, 95, 11, 3.8, [3, 6, 9]),
    makeBeam(-PI * 0.60, 85, 10, 6.1, [5, 8]),
    makeBeam(-PI * 0.18, 62, 8, 8.5, [3, 6]),
    makeBeam(-PI * 0.28, 55, 7, 11.0, [4]),
    makeBeam(-PI * 0.72, 58, 8, 13.5, [3, 5]),
    makeBeam(-PI * 0.82, 64, 8, 16.0, [4, 7]),
  ];

  // Embers along beams
  const embers = [];
  beams.forEach((b, bi) => {
    b.pts.forEach((pt, pi) => {
      if (pi > 0 && pi % 2 === 0) {
        embers.push({
          x: pt.x + Math.sin(bi * 3 + pi * 2.1) * 1.5,
          y: pt.y + Math.cos(bi * 4 + pi * 1.7) * 1,
          delay: (bi * 0.08 + pi * 0.04),
          size: 0.25 + Math.abs(Math.sin(bi + pi)) * 0.15,
        });
      }
    });
  });

  // Heat shimmer positions (along ground beams)
  const shimmers = beams.slice(3).flatMap((b, bi) =>
    b.pts.filter((_, i) => i > 1 && i % 2 === 0).map((pt, si) => ({
      x: pt.x, y: pt.y, seed: bi * 5 + si * 3,
    }))
  );

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      background: '#060e18',
      overflow: 'hidden',
    }}>
      {/* Grey stormy sky */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '65%',
        background: 'linear-gradient(180deg, #1e2228 0%, #282d33 20%, #333840 40%, #2a2f36 60%, #222830 100%)',
      }} />

      {/* Heavy storm clouds */}
      {[
        { x: 5, y: 2, w: 35, h: 18 },
        { x: 40, y: 0, w: 40, h: 20 },
        { x: 70, y: 4, w: 30, h: 16 },
        { x: 15, y: 15, w: 30, h: 14 },
        { x: 50, y: 12, w: 35, h: 16 },
        { x: -5, y: 10, w: 28, h: 12 },
        { x: 75, y: 14, w: 25, h: 12 },
        { x: 25, y: 25, w: 40, h: 10 },
      ].map((c, i) => (
        <div key={`cloud-${i}`} style={{
          position: 'absolute',
          left: `${c.x + Math.sin(progress * Math.PI * 2 + i * 1.3) * 1.5}%`,
          top: `${c.y}%`,
          width: `${c.w}%`,
          height: `${c.h}%`,
          borderRadius: '50%',
          background: `radial-gradient(ellipse at ${45 + i * 4}% ${50 + i * 3}%, 
            rgba(55,60,68,${0.9 - i * 0.04}) 0%, 
            rgba(42,48,55,${0.7 - i * 0.03}) 40%, 
            rgba(30,35,42,${0.4 - i * 0.02}) 70%, 
            transparent 100%)`,
          filter: `blur(${10 + i * 1.5}px)`,
        }} />
      ))}

      {/* Lower third — dark green playing field */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '38%',
        background: 'linear-gradient(180deg, #1a2a15 0%, #142210 40%, #0e1a0b 100%)',
      }} />

      {/* Faint treeline on horizon */}
      <svg viewBox="0 0 200 20" preserveAspectRatio="none" style={{
        position: 'absolute',
        top: '60%',
        left: 0,
        right: 0,
        height: '6%',
        opacity: 0.18,
      }}>
        {Array.from({ length: 30 }, (_, i) => {
          const x = i * 7;
          const h = 8 + Math.sin(i * 2.3) * 5 + Math.cos(i * 1.7) * 3;
          return <rect key={i} x={x} y={20 - h} width={5 + Math.sin(i) * 2} height={h} fill="#1a3018" rx="1" />;
        })}
      </svg>

      {/* Goalposts on horizon */}
      <svg viewBox="0 0 100 30" preserveAspectRatio="xMidYMax meet" style={{
        position: 'absolute',
        top: '58%',
        left: '30%',
        width: '12%',
        height: '8%',
        opacity: 0.15,
      }}>
        <line x1="10" y1="30" x2="10" y2="5" stroke="#3a4a3a" strokeWidth="2" />
        <line x1="90" y1="30" x2="90" y2="5" stroke="#3a4a3a" strokeWidth="2" />
        <line x1="10" y1="5" x2="90" y2="5" stroke="#3a4a3a" strokeWidth="2" />
      </svg>
      <svg viewBox="0 0 100 30" preserveAspectRatio="xMidYMax meet" style={{
        position: 'absolute',
        top: '59%',
        left: '65%',
        width: '8%',
        height: '5%',
        opacity: 0.10,
      }}>
        <line x1="10" y1="30" x2="10" y2="5" stroke="#3a4a3a" strokeWidth="2.5" />
        <line x1="90" y1="30" x2="90" y2="5" stroke="#3a4a3a" strokeWidth="2.5" />
        <line x1="10" y1="5" x2="90" y2="5" stroke="#3a4a3a" strokeWidth="2.5" />
      </svg>

      {/* Playground equipment sketches on horizon */}
      <svg viewBox="0 0 800 100" preserveAspectRatio="xMidYMax meet" style={{
        position: 'absolute',
        top: '52%',
        left: '3%',
        right: '3%',
        height: '16%',
        filter: 'blur(0.8px)',
        opacity: 0.4,
      }}>
        {/* Swing set */}
        <line x1="120" y1="100" x2="140" y2="25" stroke="#4a5c4a" strokeWidth="3.5" />
        <line x1="180" y1="100" x2="160" y2="25" stroke="#4a5c4a" strokeWidth="3.5" />
        <line x1="135" y1="25" x2="165" y2="25" stroke="#4a5c4a" strokeWidth="3" />
        <line x1="146" y1="25" x2="142" y2="70" stroke="#556655" strokeWidth="1.5" />
        <line x1="154" y1="25" x2="158" y2="70" stroke="#556655" strokeWidth="1.5" />
        <rect x="140" y="70" width="18" height="4" rx="1" fill="#4a5c4a" />

        {/* Slide with ladder */}
        <line x1="310" y1="100" x2="310" y2="30" stroke="#4a5c4a" strokeWidth="3" />
        <line x1="310" y1="30" x2="380" y2="100" stroke="#556655" strokeWidth="4" />
        <line x1="305" y1="48" x2="315" y2="48" stroke="#4a5c4a" strokeWidth="2.5" />
        <line x1="305" y1="63" x2="315" y2="63" stroke="#4a5c4a" strokeWidth="2.5" />
        <line x1="305" y1="78" x2="315" y2="78" stroke="#4a5c4a" strokeWidth="2.5" />

        {/* Climbing frame */}
        <rect x="495" y="35" width="65" height="65" rx="2" fill="none" stroke="#4a5c4a" strokeWidth="3" />
        <line x1="495" y1="57" x2="560" y2="57" stroke="#4a5c4a" strokeWidth="2" />
        <line x1="495" y1="78" x2="560" y2="78" stroke="#4a5c4a" strokeWidth="2" />
        <line x1="527" y1="35" x2="527" y2="100" stroke="#4a5c4a" strokeWidth="2" />

        {/* Roundabout */}
        <ellipse cx="680" cy="82" rx="35" ry="10" fill="none" stroke="#4a5c4a" strokeWidth="2.5" />
        <line x1="680" y1="100" x2="680" y2="72" stroke="#4a5c4a" strokeWidth="3" />
      </svg>

      {/* Origin glow — reaching up and stretching out from bottom */}
      <div style={{
        position: 'absolute',
        bottom: '-10%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        height: '70%',
        background: `radial-gradient(ellipse at 50% 95%, 
          rgba(201,168,76,${0.7 * Math.min(1, progress * 2.5)}) 0%, 
          rgba(232,184,74,${0.45 * Math.min(1, progress * 2.5)}) 15%,
          rgba(201,168,76,${0.25 * Math.min(1, progress * 2.5)}) 35%, 
          rgba(160,120,40,${0.12 * Math.min(1, progress * 2.5)}) 55%, 
          rgba(201,168,76,${0.04 * Math.min(1, progress * 2.5)}) 75%, 
          transparent 100%)`,
        filter: 'blur(15px)',
        pointerEvents: 'none',
        zIndex: 5,
      }} />

      {/* Wide stretching glow across bottom */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '45%',
        background: `linear-gradient(to top, 
          rgba(201,168,76,${0.15 * Math.min(1, progress * 2)}) 0%, 
          rgba(201,168,76,${0.06 * Math.min(1, progress * 2)}) 40%, 
          transparent 100%)`,
        filter: 'blur(20px)',
        pointerEvents: 'none',
        zIndex: 3,
      }} />

      {/* THE BEAMS — 7 jagged gold bolts erupting from origin */}
      <svg viewBox="0 0 100 102" preserveAspectRatio="none" style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible',
      }}>
        <defs>
          <filter id="beamGlow3">
            <feGaussianBlur stdDeviation="0.6" result="b1" />
            <feGaussianBlur stdDeviation="1.5" result="b2" />
            <feMerge>
              <feMergeNode in="b2" />
              <feMergeNode in="b1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="trenchGlow">
            <feGaussianBlur stdDeviation="1.2" />
          </filter>
        </defs>

        {beams.map((beam, i) => {
          const delay = i * 0.08;
          const lineP = Math.max(0, Math.min(1, (progress * 2.2) - delay));
          const eased = 1 - Math.pow(1 - lineP, 3);
          const flicker = 0.75 + Math.sin(progress * PI * 10 + i * 2.5) * 0.25;

          return (
            <g key={i}>
              {/* Layer 1: Dark burn trench */}
              <path d={beam.d} stroke="rgba(10,6,2,0.7)" strokeWidth="3.5"
                fill="none" filter="url(#trenchGlow)"
                strokeDasharray="600" strokeDashoffset={600 - eased * 600} strokeLinecap="round" />
              {/* Layer 2: Amber glow */}
              <path d={beam.d} stroke={`rgba(160,120,40,${0.7 * eased * flicker})`} strokeWidth="2.2"
                fill="none" filter="url(#beamGlow3)"
                strokeDasharray="600" strokeDashoffset={600 - eased * 600} strokeLinecap="round" />
              {/* Layer 3: Bright gold core */}
              <path d={beam.d} stroke={`rgba(201,168,76,${0.95 * eased})`} strokeWidth="1.0"
                fill="none" filter="url(#beamGlow3)"
                strokeDasharray="600" strokeDashoffset={600 - eased * 600} strokeLinecap="round" />
              {/* Layer 4: White-hot centre */}
              <path d={beam.d} stroke={`rgba(255,245,212,${0.85 * eased * flicker})`} strokeWidth="0.35"
                fill="none"
                strokeDasharray="600" strokeDashoffset={600 - eased * 600} strokeLinecap="round" />

              {/* Forks */}
              {beam.forks.map((fd, fi) => {
                const forkP = Math.max(0, Math.min(1, (progress * 2.5) - delay - 0.15));
                const forkE = 1 - Math.pow(1 - forkP, 2);
                return (
                  <g key={fi}>
                    <path d={fd} stroke="rgba(10,6,2,0.5)" strokeWidth="2"
                      fill="none" filter="url(#trenchGlow)"
                      strokeDasharray="200" strokeDashoffset={200 - forkE * 200} strokeLinecap="round" />
                    <path d={fd} stroke={`rgba(160,120,40,${0.6 * forkE})`} strokeWidth="1.4"
                      fill="none" filter="url(#beamGlow3)"
                      strokeDasharray="200" strokeDashoffset={200 - forkE * 200} strokeLinecap="round" />
                    <path d={fd} stroke={`rgba(201,168,76,${0.8 * forkE})`} strokeWidth="0.6"
                      fill="none"
                      strokeDasharray="200" strokeDashoffset={200 - forkE * 200} strokeLinecap="round" />
                    <path d={fd} stroke={`rgba(255,245,212,${0.6 * forkE * flicker})`} strokeWidth="0.2"
                      fill="none"
                      strokeDasharray="200" strokeDashoffset={200 - forkE * 200} strokeLinecap="round" />
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Ember dots along beams */}
        {embers.map((em, i) => {
          const ep = Math.max(0, Math.min(1, (progress * 2.5) - em.delay));
          if (ep <= 0) return null;
          const rise = ep * (1.5 + Math.sin(i * 2.3) * 0.8);
          const drift = Math.sin(progress * PI * 5 + i * 1.9) * 0.8;
          const fade = ep > 0.6 ? 1 - ((ep - 0.6) / 0.4) : 1;
          return (
            <circle key={`em-${i}`}
              cx={em.x + drift} cy={em.y - rise} r={em.size}
              fill={`rgba(232,184,74,${fade * 0.9})`}
            />
          );
        })}
      </svg>

      {/* Heat shimmer lines rising from ground scorch marks */}
      {shimmers.map((sh, i) => {
        const shP = Math.max(0, Math.min(1, progress * 2 - i * 0.1));
        if (shP <= 0) return null;
        return (
          <div key={`sh-${i}`} style={{
            position: 'absolute',
            left: `${sh.x}%`,
            top: `${sh.y - 8}%`,
            width: '1px',
            height: '8%',
            background: `linear-gradient(to top, rgba(201,168,76,${0.08 * shP}), transparent)`,
            filter: 'blur(2px)',
            transform: `translateX(${Math.sin(progress * PI * 4 + sh.seed) * 6}px) scaleX(${1 + Math.sin(progress * PI * 6 + sh.seed) * 0.5})`,
            pointerEvents: 'none',
          }} />
        );
      })}

      {/* Text overlay — at the top over grey clouds */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '22%',
        background: 'linear-gradient(to bottom, rgba(6,14,24,0.6) 0%, rgba(6,14,24,0.3) 60%, transparent 100%)',
        zIndex: 15,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        top: '6%',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        zIndex: 20,
      }}>
        {texts.map((text, i) => {
          const textProgress = Math.max(0, Math.min(1, (progress * 2.5) - i * 0.5));
          return (
            <p key={i} style={{
              fontFamily: "'Caveat', cursive",
              fontSize: 'clamp(20px, 2.8vw, 28px)',
              color: '#c9a84c',
              textShadow: '0 0 20px rgba(201,168,76,0.7), 0 2px 6px rgba(0,0,0,0.9)',
              opacity: textProgress,
              textAlign: 'center',
              maxWidth: '80%',
              margin: 0,
            }}>
              {text}
            </p>
          );
        })}
      </div>
    </div>
  );
}

// Shot 4: Two dancing flames
function Shot04FireEyes({ progress }) {
  const text = SHOT_TEXT[4][0];
  const pulseIntensity = progress > 0.3 && progress < 0.5 ? 1.3 : 1;
  
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      
    }}>
      {/* Flames */}
      <div style={{ display: 'flex', gap: '120px', marginBottom: '60px' }}>
        {[0, 1].map(i => (
          <div
            key={i}
            style={{
              width: '60px',
              height: '120px',
              position: 'relative',
              transform: `scale(${pulseIntensity})`,
              transition: 'transform 0.3s ease-out',
            }}
          >
            <svg viewBox="0 0 60 120" style={{ width: '100%', height: '100%' }}>
              <defs>
                <linearGradient id={`flame${i}`} x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#ff4400" />
                  <stop offset="50%" stopColor="#ff8800" />
                  <stop offset="100%" stopColor="#ffcc00" />
                </linearGradient>
                <filter id={`flameGlow${i}`}>
                  <feGaussianBlur stdDeviation="5" />
                </filter>
              </defs>
              <ellipse
                cx="30"
                cy="90"
                rx="25"
                ry="40"
                fill={`url(#flame${i})`}
                filter={`url(#flameGlow${i})`}
                style={{
                  animation: `flicker ${1.5 + i * 0.3}s ease-in-out infinite`,
                  transformOrigin: 'center bottom',
                }}
              />
              <ellipse
                cx="30"
                cy="85"
                rx="15"
                ry="30"
                fill="#ffee88"
                style={{
                  animation: `flicker ${1.2 + i * 0.2}s ease-in-out infinite`,
                  transformOrigin: 'center bottom',
                }}
              />
            </svg>
          </div>
        ))}
      </div>
      
      {/* Text */}
      <p style={{
        fontFamily: "'Caveat', cursive",
        fontSize: 'clamp(20px, 3vw, 30px)',
        color: '#c9a84c',
        textShadow: '0 0 15px rgba(201,168,76,0.6), 0 2px 4px rgba(0,0,0,0.8)',
        opacity: Math.min(1, progress * 2),
        textAlign: 'center',
        maxWidth: '80%',
      }}>
        {text}
      </p>
    </div>
  );
}

// Shot 5: Carnival whirl
function Shot05Carnival({ progress }) {
  const texts = SHOT_TEXT[5];

  // Split all text lines into words with their target positions
  const allWords = [];
  texts.forEach((line, lineIdx) => {
    const words = line.split(' ');
    words.forEach((word, wordIdx) => {
      allWords.push({
        word,
        lineIdx,
        wordIdx,
        // Whirl start: random orbit in the carnival
        startAngle: (allWords.length * 47 + wordIdx * 73) % 360,
        startDist: 80 + (allWords.length * 31) % 120,
        startRotation: (allWords.length * 67) % 360 - 180,
        // Each word settles at a slightly different time
        settleTime: 0.2 + lineIdx * 0.08 + wordIdx * 0.015,
      });
    });
  });

  const allWords_length = allWords.length; // for reference

  const items = Array.from({ length: 20 }, (_, i) => ({
    type: ['feather', 'jewel', 'ribbon'][i % 3],
    angle: (i * 18) + progress * 360,
    distance: 100 + (i % 5) * 40,
    size: 20 + (i % 3) * 15,
    color: ['#c9a84c', '#ff6b9d', '#4ecdc4', '#9b59b6', '#e74c3c'][i % 5],
  }));
  
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Spinning items */}
      {items.map((item, i) => {
        const x = Math.cos((item.angle * Math.PI) / 180) * item.distance;
        const y = Math.sin((item.angle * Math.PI) / 180) * item.distance * 0.6;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${y}px)`,
              width: `${item.size}px`,
              height: `${item.size * 2}px`,
              transform: `rotate(${item.angle}deg)`,
              opacity: 0.7 + Math.sin(progress * Math.PI * 2 + i) * 0.3,
            }}
          >
            {item.type === 'feather' && (
              <svg viewBox="0 0 20 40" style={{ width: '100%', height: '100%' }}>
                <path
                  d="M10,2 C15,5 18,15 18,25 C18,35 14,38 10,38 C6,38 2,35 2,25 C2,15 5,5 10,2"
                  fill={item.color}
                  opacity="0.8"
                />
              </svg>
            )}
            {item.type === 'jewel' && (
              <div style={{
                width: '100%',
                height: '50%',
                background: item.color,
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                boxShadow: `0 0 10px ${item.color}`,
              }} />
            )}
            {item.type === 'ribbon' && (
              <svg viewBox="0 0 20 60" style={{ width: '100%', height: '100%' }}>
                <path
                  d={`M5,0 Q15,20 5,40 Q-5,60 5,60`}
                  stroke={item.color}
                  strokeWidth="3"
                  fill="none"
                />
              </svg>
            )}
          </div>
        );
      })}
      
      {/* Words — spin in whirl then settle into sentences */}
      {allWords.map((w, i) => {
        const settle = Math.max(0, Math.min(1, (progress - w.settleTime) / 0.25));
        const eased = settle * settle * (3 - 2 * settle); // smoothstep

        // Whirl position
        const whirlAngle = (w.startAngle + progress * 320) * Math.PI / 180;
        const whirlX = Math.cos(whirlAngle) * w.startDist;
        const whirlY = Math.sin(whirlAngle) * w.startDist * 0.6;
        const whirlRot = w.startRotation + progress * 400;

        // Settled position — calculate based on character widths
        const lineWords = texts[w.lineIdx].split(' ');
        const charWidth = 10; // approx px per char in Caveat at this size
        const spaceWidth = 8;
        const lineWidth = lineWords.reduce((sum, lw) => sum + lw.length * charWidth + spaceWidth, -spaceWidth);
        let xBefore = 0;
        for (let wi = 0; wi < w.wordIdx; wi++) {
          xBefore += lineWords[wi].length * charWidth + spaceWidth;
        }
        const wordCenter = xBefore + (w.word.length * charWidth) / 2;
        const settledX = wordCenter - lineWidth / 2;
        const settledY = 160 + w.lineIdx * 34;

        const x = whirlX + (settledX - whirlX) * eased;
        const y = whirlY + (settledY - whirlY) * eased;
        const rot = whirlRot * (1 - eased);
        const appear = Math.max(0, Math.min(1, (progress - i * 0.015) * 4));

        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: `translate(calc(-50% + ${x}px), ${y}px) rotate(${rot}deg)`,
              fontFamily: "'Caveat', cursive",
              fontSize: 'clamp(18px, 2.5vw, 26px)',
              color: '#c9a84c',
              textShadow: eased > 0.9
                ? '0 0 15px rgba(201,168,76,0.6), 0 2px 8px rgba(0,0,0,0.9)'
                : `0 0 ${10 + (1 - eased) * 12}px rgba(201,168,76,0.5)`,
              opacity: appear,
              whiteSpace: 'nowrap',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            {w.word}
          </span>
        );
      })}
      })}
    </div>
  );
}

// Shot 6: Star explosion
function Shot06Stars({ progress }) {
  const texts = SHOT_TEXT[6];
  const stars = Array.from({ length: 50 }, (_, i) => ({
    angle: (i * 7.2) + Math.random() * 30,
    speed: 0.5 + Math.random() * 1.5,
    size: 2 + Math.random() * 4,
    color: Math.random() > 0.3 ? '#c9a84c' : '#aaddff',
    delay: Math.random() * 0.3,
  }));
  
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      
    }}>
      {/* Stars */}
      {stars.map((star, i) => {
        const burstProgress = Math.max(0, Math.min(1, (progress - star.delay) * 2));
        const distance = burstProgress * star.speed * 300;
        const x = Math.cos((star.angle * Math.PI) / 180) * distance;
        const y = Math.sin((star.angle * Math.PI) / 180) * distance;
        const settled = progress > 0.6;
        const drift = settled ? Math.sin(progress * Math.PI * 2 + i) * 10 : 0;
        
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `calc(50% + ${x + drift}px)`,
              top: `calc(50% + ${y}px)`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              borderRadius: '50%',
              background: star.color,
              boxShadow: `0 0 ${star.size * 2}px ${star.color}`,
              animation: settled ? `twinkle ${2 + i % 3}s ease-in-out infinite` : 'none',
            }}
          />
        );
      })}
      
      {/* Text overlay */}
      <div style={{
        position: 'absolute',
        bottom: '12%',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        zIndex: 10,
      }}>
        {texts.map((text, i) => {
          const textProgress = Math.max(0, Math.min(1, (progress * 3) - i * 0.3));
          return (
            <p key={i} style={{
              fontFamily: "'Caveat', cursive",
              fontSize: 'clamp(18px, 2.5vw, 26px)',
              color: '#c9a84c',
              textShadow: '0 0 15px rgba(201,168,76,0.6), 0 2px 8px rgba(0,0,0,0.9)',
              opacity: textProgress,
              textAlign: 'center',
              maxWidth: '80%',
              margin: 0,
            }}>
              {text}
            </p>
          );
        })}
      </div>
    </div>
  );
}

// Shot 7: Leaves fly in and unfurl to form the box
function Shot07SpiralsBox({ progress, boxWaiting, boxOpening, onBoxTap }) {
  const texts = SHOT_TEXT[7];
  const [leafFaded, setLeafFaded] = useState(false);
  
  // Trigger leaf fade after voiceover ends
  useEffect(() => {
    if (!boxWaiting) return;
    const t = setTimeout(() => setLeafFaded(true), 200);
    return () => clearTimeout(t);
  }, [boxWaiting]);

  // Create leaves that will form the box outline
  const numLeaves = 24;
  const boxWidth = 200;
  const boxHeight = 120;
  
  // Box outline positions (leaves will land here)
  const getBoxPosition = (index) => {
    const perSide = numLeaves / 4;
    const side = Math.floor(index / perSide);
    const posOnSide = (index % perSide) / perSide;
    
    switch(side) {
      case 0: // Top
        return { x: -boxWidth/2 + boxWidth * posOnSide, y: -boxHeight/2 };
      case 1: // Right
        return { x: boxWidth/2, y: -boxHeight/2 + boxHeight * posOnSide };
      case 2: // Bottom
        return { x: boxWidth/2 - boxWidth * posOnSide, y: boxHeight/2 };
      case 3: // Left
        return { x: -boxWidth/2, y: boxHeight/2 - boxHeight * posOnSide };
      default:
        return { x: 0, y: 0 };
    }
  };
  
  // Starting positions (scattered around)
  const getStartPosition = (index) => {
    const angle = (index * 137.5) * Math.PI / 180; // Golden angle
    const distance = 400 + Math.random() * 200;
    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      rotation: Math.random() * 360
    };
  };

  const leaves = Array.from({ length: numLeaves }, (_, i) => ({
    start: getStartPosition(i),
    end: getBoxPosition(i),
    delay: (i / numLeaves) * 0.4,
    color: ['#c9a84c', '#d4a84c', '#b89a3c', '#e0b050'][i % 4],
    size: 18 + Math.random() * 8,
  }));

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Flying leaves — fade out once box is waiting */}
      {leaves.map((leaf, i) => {
        const leafProgress = Math.max(0, Math.min(1, (progress - leaf.delay) * 1.8));
        const eased = 1 - Math.pow(1 - leafProgress, 3);
        
        const x = leaf.start.x + (leaf.end.x - leaf.start.x) * eased;
        const y = leaf.start.y + (leaf.end.y - leaf.start.y) * eased;
        
        const spinRotation = leaf.start.rotation + (1 - eased) * 720;
        const settleRotation = leaf.end.x > 0 ? 90 : -90;
        const rotation = eased < 0.8 ? spinRotation : spinRotation + (settleRotation - spinRotation) * ((eased - 0.8) / 0.2);
        
        const unfurl = Math.min(1, eased * 1.5);
        const scaleX = 0.3 + unfurl * 0.7;
        const settled = eased > 0.9;
        
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: `translate(${x}px, ${y}px) rotate(${rotation}deg) scaleX(${scaleX})`,
              width: `${leaf.size}px`,
              height: `${leaf.size * 1.8}px`,
              filter: settled && !leafFaded ? `drop-shadow(0 0 6px ${leaf.color})` : 'none',
              opacity: leafFaded ? 0 : 0.85,
              transition: leafFaded ? `opacity ${1.5 + i * 0.05}s ease-out` : (settled ? 'filter 0.5s' : 'none'),
            }}
          >
            <svg viewBox="0 0 24 40" style={{ width: '100%', height: '100%' }}>
              <path
                d="M12,2 
                   C16,6 20,14 20,22 
                   C20,30 16,36 12,38 
                   C8,36 4,30 4,22 
                   C4,14 8,6 12,2 Z"
                fill={leaf.color}
                opacity={0.85}
              />
              <path
                d="M12,4 L12,36 
                   M12,10 L8,14 
                   M12,16 L16,20 
                   M12,22 L8,26 
                   M12,28 L16,32"
                stroke="rgba(0,0,0,0.2)"
                strokeWidth="0.8"
                fill="none"
              />
              <path
                d="M10,8 C12,12 12,18 10,24"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>
        );
      })}
      
      {/* Golden box outline */}
      <div
        data-testid="golden-box"
        onClick={boxWaiting && !boxOpening ? onBoxTap : undefined}
        style={{
          position: 'absolute',
          width: `${boxWidth}px`,
          height: `${boxHeight}px`,
          border: `2px solid rgba(201, 168, 76, ${boxWaiting ? 1 : 0})`,
          borderRadius: '4px',
          cursor: boxWaiting && !boxOpening ? 'pointer' : 'default',
          pointerEvents: boxWaiting && !boxOpening ? 'auto' : 'none',
          boxShadow: boxWaiting
            ? '0 0 20px rgba(201,168,76,0.4), inset 0 0 15px rgba(201,168,76,0.2)'
            : 'none',
          animation: boxWaiting && !boxOpening ? 'boxPulse 2s ease-in-out infinite' : 'none',
          zIndex: 20,
        }}
      />

      {/* Box opening glow — golden light expanding outward */}
      {boxOpening && (
        <div style={{
          position: 'absolute',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,168,76,0.9) 0%, rgba(201,168,76,0.4) 40%, rgba(6,14,24,0) 70%)',
          animation: 'boxOpenGlow 1.8s ease-out forwards',
          zIndex: 50,
        }} />
      )}

      {/* "tap to open" hint */}
      {boxWaiting && !boxOpening && (
        <p style={{
          position: 'absolute',
          marginTop: `${boxHeight + 40}px`,
          fontFamily: "'Caveat', cursive",
          fontSize: 'clamp(16px, 2vw, 22px)',
          color: 'rgba(201, 168, 76, 0.7)',
          animation: 'tapPulse 2s ease-in-out infinite',
          zIndex: 20,
        }}>
          tap to open
        </p>
      )}
      
      {/* Text overlay — fades out when box is waiting */}
      <div style={{
        position: 'absolute',
        bottom: '10%',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        zIndex: 10,
        opacity: boxWaiting ? 0 : 1,
        transition: boxWaiting ? 'opacity 1.5s ease-out' : 'none',
      }}>
        {texts.map((text, i) => {
          const textProgress = Math.max(0, Math.min(1, (progress * 3) - i * 0.3));
          return (
            <p key={i} style={{
              fontFamily: "'Caveat', cursive",
              fontSize: 'clamp(18px, 2.5vw, 26px)',
              color: '#c9a84c',
              textShadow: '0 0 15px rgba(201,168,76,0.6), 0 2px 8px rgba(0,0,0,0.9)',
              opacity: textProgress,
              textAlign: 'center',
              maxWidth: '80%',
              margin: 0,
            }}>
              {text}
            </p>
          );
        })}
      </div>
    </div>
  );
}
